/* ============================================================
   useMarkdown — markdown → sanitized HTML with source-line info

   Ported from prototype/reader.html section 3. markdown-it parses
   to tokens; we render each top-level block separately and wrap it
   in a `.md-block[data-source-line-start][data-source-line-end]`
   div so comments anchor to source lines stably across re-renders.
   DOMPurify sanitizes (allowing those data attrs); highlight.js
   colors `pre code` blocks afterward.

   Vanilla markdown + code highlighting only for v1 — no mermaid,
   no directives (parked in core-buildout-planning/v6.md).
   ============================================================ */

import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'
import hljs from 'highlight.js'

let md: MarkdownIt | null = null
function getMd(): MarkdownIt {
  if (!md) md = new MarkdownIt({ html: false, linkify: true, typographer: true })
  return md
}

/**
 * Render markdown to sanitized HTML, wrapping each top-level block in a
 * `.md-block` div carrying its 1-based source-line span.
 */
export function renderMarkdownWithLineInfo(src: string): string {
  const mdi = getMd()
  const tokens = mdi.parse(src || '', {})
  const env = {}
  let html = ''

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i]
    if (tok.level !== 0) continue

    let slice
    const map = tok.map

    if (tok.nesting === 1) {
      // Find matching close at the same nesting level.
      let depth = 1
      let j = i + 1
      while (j < tokens.length && depth > 0) {
        if (tokens[j].level === 0 && tokens[j].nesting === 1) depth++
        else if (tokens[j].level === 0 && tokens[j].nesting === -1) depth--
        if (depth === 0) break
        j++
      }
      slice = tokens.slice(i, j + 1)
      i = j
    } else if (tok.nesting === 0) {
      slice = [tok]
    } else {
      continue
    }

    const inner = mdi.renderer.render(slice, mdi.options, env)

    if (map && map.length === 2) {
      const startLine = map[0] + 1
      const endLine = map[1]
      html += `<div class="md-block" data-source-line-start="${startLine}" data-source-line-end="${endLine}">${inner}</div>`
    } else {
      html += inner
    }
  }

  return DOMPurify.sanitize(html, {
    ADD_ATTR: ['data-source-line-start', 'data-source-line-end'],
  })
}

/** Syntax-highlight all `pre code` blocks within a rendered root. */
export function highlightCodeBlocks(root: HTMLElement): void {
  root.querySelectorAll('pre code').forEach((block) => {
    try {
      hljs.highlightElement(block as HTMLElement)
    } catch {
      /* ignore unknown languages / highlight failures */
    }
  })
}

export function useMarkdown() {
  return {
    renderMarkdownWithLineInfo,
    highlightCodeBlocks,
  }
}
